import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Users } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useSubjects } from "../SubjectContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import AddPersonModal from "./AddPersonModal";

const COLOR_CLASS = {
  teal: "bg-teal-600",
  amber: "bg-amber-600",
  rose: "bg-rose-600",
  indigo: "bg-indigo-600",
  emerald: "bg-emerald-600",
  sky: "bg-sky-600",
  violet: "bg-violet-600",
  orange: "bg-orange-600",
};

/** True on `/people/:subjectId` (not the people list). */
function isPeopleProfilePath(pathname) {
  return /^\/people\/[^/]+/.test(pathname || "");
}

export function subjectInitial(name) {
  const n = (name || "?").trim();
  return n ? n.charAt(0).toUpperCase() : "?";
}

export function SubjectAvatar({ subject, size = "md" }) {
  const dim =
    size === "xs"
      ? "h-6 w-6 text-[11px]"
      : size === "sm"
        ? "h-7 w-7 text-xs"
        : "h-8 w-8 text-sm";
  const color = COLOR_CLASS[subject?.avatar_color] || "bg-primary";
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full text-white font-medium shrink-0 ${dim} ${color}`}
      aria-hidden
    >
      {subjectInitial(subject?.display_name)}
    </span>
  );
}

export default function SubjectSwitcher() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    subjects,
    activeSubject,
    setActiveSubjectId,
    atLimit,
    refreshSubjects,
  } = useSubjects();
  const [addOpen, setAddOpen] = useState(false);

  const selectSubject = (subjectId) => {
    setActiveSubjectId(subjectId);
    // Profile page is keyed by URL; keep it in sync with the switcher.
    if (subjectId && isPeopleProfilePath(location.pathname)) {
      navigate(`/people/${subjectId}`);
    }
  };

  if (!activeSubject) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="subject-switcher"
            className="w-full flex items-center gap-2 rounded-lg px-2 py-2 text-left hover:bg-muted/60 transition-colors"
          >
            <SubjectAvatar subject={activeSubject} />
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium truncate">
                {activeSubject.display_name}
              </span>
              {activeSubject.age_display && (
                <span className="block text-xs text-muted-foreground truncate">
                  {activeSubject.age_display}
                </span>
              )}
            </span>
            <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {subjects.map((s) => (
            <DropdownMenuItem
              key={s.subject_id}
              onClick={() => selectSubject(s.subject_id)}
              className="gap-2"
            >
              <SubjectAvatar subject={s} size="sm" />
              <span className="truncate">
                {s.display_name}
                {s.age_display ? ` · ${s.age_display}` : ""}
              </span>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={atLimit}
            onClick={() => !atLimit && setAddOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {t("subjects.addPerson")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => navigate("/people")}
            className="gap-2"
          >
            <Users className="h-4 w-4" />
            {t("subjects.managePeople")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AddPersonModal
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={async (created) => {
          await refreshSubjects();
          if (created?.subject_id) selectSubject(created.subject_id);
        }}
      />
    </>
  );
}
